import React from 'react';
import V2Layout from '../Layout';
import { Box, Button, Checkbox, FormControlLabel, Grid, IconButton, LinearProgress, MenuItem, Paper, Stack, Tab, Tabs, TextField, Typography } from '@mui/material';
import { translate } from '../../../i18n/customI18nProvider';
import Swal from 'sweetalert2';
import { convertMessageCodeToMessage, generateRandomString } from '../../../util';
import { getRight } from '../../../services/storage';
import { getCategoryDetails, createCategory, saveCategoryDetails, generateQrsFile } from '../../../services/wristbandCategory.service';
import InputFile from '../../../components/v2/common/input/InputFile';
import * as XLSX from 'xlsx';
import CloseIcon from '@mui/icons-material/Close';
import config from '../../../config';
import FileOpenIcon from '@mui/icons-material/FileOpen';

import { importWristbandPackets } from '../../../services/wristbandCategory.service';

class ImportWristbandPacket extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            current_right: getRight(),
            loading: false,
            form_error: {},
            name: '',
            excel: null,
            tabValue: 0,
            category_id: null,
            name: '',
            type: 'paper',
            title: '',
            text: '',
        }
    }

    async setStateAsync(state) {
        return new Promise((resolve) => {
            this.setState(state, resolve);
        });
    }

    async componentDidMount() {
        let category_id = this.props?.match?.params?.category_id ? this.props.match.params.category_id : null;

        await this.setStateAsync({
            loading: true,
            category_id: category_id,
        });
        
        await this.getCategory();
            
        
        this.setState({ loading: false });
    }   

    async getCategory() {
        let category = await getCategoryDetails(this.state.category_id);
        if (category.error) {
            Swal.fire({
                icon: 'error',
                title: translate('custom.error'),
                text: category.error_code ? convertMessageCodeToMessage(category.error_code) : category.error
            });
            return;
        }

        await this.setStateAsync({
            name: category.name ? category.name : '',
            type: category.type ? category.type : 'paper',
            title: category.name + " - " + translate('custom.import_wristband_packet')
        });
    }

    isTabValid(tab) {
        const errors = [];
        if(tab.numberCol === '' || tab.uidCol === '') {
            errors.push(translate('custom.please_select_number_and_uid_columns_in_sheet') + ` "${tab.sheetName}"`);
        }
        if(tab.braceletsPerPack == '' || isNaN(tab.braceletsPerPack) || tab.braceletsPerPack <= 0) {
            errors.push(translate('custom.please_enter_a_valid_number_of_bracelets_per_pack_in_sheet') + ` "${tab.sheetName}"`);
        }
        if(tab.ranges != null) {
            for(const range of tab.ranges) {
                const from = parseInt(range.from);
                const to = parseInt(range.to);
                if(range.from === '' || isNaN(from) || from < 1 || range.to === '' || isNaN(to) || to < from)
                {
                    errors.push(translate('custom.invalid_selection_range_in_sheet') + ` "${tab.sheetName}"`);
                }
            }
        }
        return {valid: errors.length === 0, errors: errors};
    }

    isFormValid() {
        const errors = [];
        
        if(!this.state.excel || this.state.excel.sheets.length === 0) {
            Swal.fire({
                icon: 'error',
                title: translate('custom.error'),
                text: translate('custom.select_an_excel_file_with_at_least_one_sheet')
            })
            return false;
        }

        for(const sheet of this.state.excel?.sheetsData) {
            let tabValidation = this.isTabValid(sheet);
            errors.push(...tabValidation.errors);
        }

        if(errors.length > 0) {
            const errorText = errors.join('<br><br>'); 
            Swal.fire({
                icon: 'error',
                title: translate('custom.error'),
                html: errorText
            })
            return false;
        }
        return true;
    }

    async getPacketsFromSheetData(sheetData, rawData) {
        const workbook = XLSX.read(rawData);
        const sheet = workbook.Sheets[sheetData.sheetName];
        const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 });

        let startRow = 1; // Default start row (after header)
        if(!sheetData.hasHeader) {
            startRow = 0; // No header, start from first row
        }
        
        const numberColIndex = rows[0].indexOf(sheetData.numberCol);
        const uidColIndex = rows[0].indexOf(sheetData.uidCol);

        let selectedRows = [];
        if(sheetData.ranges != null) {
            for(const range of sheetData.ranges) {
                const from = parseInt(range.from);
                let to = parseInt(range.to);
                let started = false;
                for(let i = startRow; i < rows.length; i++) {
                    if(rows[i][numberColIndex] == from) started = true;
                    if(started) {
                        selectedRows.push(i);
                    }
                    if(rows[i][numberColIndex] == to) {
                        break;
                    }
                }
            }
        } else {
            // All rows
            for(let i = startRow; i < rows.length; i++) {
                selectedRows.push(i);
            }
        }


        const packets = [];
        let countWristband = 0;
        let packetId = 0;
        packets.push({
            id: packetId, 
            wristbands: [],
            type: sheetData.type,
            braceletsPerPack: sheetData.braceletsPerPack
        })
        for(const rowIndex of selectedRows) {
            if(packets[packetId].wristbands.length >= sheetData.braceletsPerPack) {
                packetId++;
                await new Promise(r => setTimeout(r, 0)); // Let the browser breathe to avoid freezing
                packets.push({
                    id: packetId, 
                    wristbands: [],
                    type: sheetData.type,
                    braceletsPerPack: sheetData.braceletsPerPack
                })
            }
            const row = rows[rowIndex];
            if(!row) continue; // Skip empty rows
            const number = row[numberColIndex];
            const uid = row[uidColIndex];
            if(number && uid) {
                packets[packetId].wristbands.push({
                    number: number,
                    uid: uid,
                });
            }
        }

        return packets;
    }

    async import() {

        if (!this.isFormValid()) return;

        await this.setStateAsync({ loading: true });
        
        await new Promise(resolve => setTimeout(resolve, 10));

        const packets = [];

        for(const sheetData of this.state.excel.sheetsData) {
            const sheetPackets = await this.getPacketsFromSheetData(sheetData, this.state.excel.data);
            packets.push(...sheetPackets);
        }

        
        const confirm_import = await Swal.fire({
            icon: 'warning',
            title: translate('custom.warning'),
            text: translate('custom.are_you_sure_you_want_to_import') + ` ${packets.length} ` + translate('custom.wristband_packets') + '?',
            showCancelButton: true,
            confirmButtonText: translate('custom.import'),
            cancelButtonText: translate('custom.cancel'),
            confirmButtonColor: config.baseColor
        });

        if (!confirm_import.isConfirmed) {
            this.setState({ loading: false });
            return;
        }

        // Send packets to backend
        const maxPacket = 10; // Max packets per request to avoid timeout
        const responsePackets = [];

        const importId = generateRandomString(12);
        for (let i = 0; i < packets.length / maxPacket; i++) {
            const part = packets.slice(i * maxPacket, (i + 1) * maxPacket);
            const response = await importWristbandPackets(part, this.state.category_id, importId);
            
            if (response.error) {
                Swal.fire({
                    icon: 'error',
                    title: translate('custom.error'),
                    text: response.error_code ? convertMessageCodeToMessage(response.error_code) : response.error
                });
                this.setState({ loading: false });
                return;
            }

            responsePackets.push(...response.packets);
        }

        const generate_qrs_file = await Swal.fire({
            icon: 'success',
            title: translate('custom.success'),
            text: translate('custom.wristband_packets_imported_successfully'),
            showCancelButton: false,
            confirmButtonText: translate('custom.generate_qrs_file'),
        });

        if (generate_qrs_file.isConfirmed) {
            const file = await generateQrsFile(this.state.name, responsePackets);
            const url = URL.createObjectURL(file);
            const a = document.createElement('a');
            a.href = url;
            a.download = file.name;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        }
        this.props.history.push('/v2/admin/wristbands/' + this.state.category_id + '/show');    
    }

    async sheetToJsonChunked(sheet) {
        const range = XLSX.utils.decode_range(sheet['!ref']);
        const rows = [];
        
        for (let R = range.s.r; R <= range.e.r; ++R) {
            const row = [];
            for (let C = range.s.c; C <= range.e.c; ++C) {
                const cellAddress = { c: C, r: R };
                const cellRef = XLSX.utils.encode_cell(cellAddress);
                row.push(sheet[cellRef] ? sheet[cellRef].v : null);
            }
            rows.push(row);

            // Let the browser breathe every 100 lines
            if (R % 100 === 0) await new Promise(r => setTimeout(r, 0));
        }

        return rows;
    }

    async analyzeExcelFile(workbook) {
        const result = [];
        for(const sheetName of workbook.SheetNames) {
            const sheet = workbook.Sheets[sheetName];

            const rows = await this.sheetToJsonChunked(sheet);

            if (rows.length === 0) {
                result.push({
                    sheetName,
                    headerValues: [],
                    rowCount: 0,
                });
                return;
            }

            const headerValues = rows[0];
            const rowCount = rows.length;

            result.push({
                sheetName,
                headerValues,
                rowCount,
            });
        }

        return result;
    }

    async handleFileChange(event) {
        const file = event.target.files[0];
        await this.setStateAsync({ loading: true });        
        await new Promise(resolve => setTimeout(resolve, 10)); 
        
        const data = await file.arrayBuffer();
        const workbook = XLSX.read(data);
        const excelData = await this.analyzeExcelFile(workbook);

        this.setState({
            excel: {
                fileName: file.name,
                data: data,
                sheets: excelData,
                sheetsData: excelData.map(sheet => ({
                    numberCol: '',
                    uidCol: '',
                    braceletsPerPack: this.state.type == 'fabric' ? 100 : 125,
                    range: null,
                    sheetName: sheet.sheetName,
                    hasHeader: false
                }))
            }
        });

        this.setState({loading: false});
    }

    a11yProps(index) {
        return {
            id: `simple-tab-${index}`,
            'aria-controls': `simple-tabpanel-${index}`,
        };
    }

    /**  Component for Sheet Tab Panel  */
    SheetTabPanel = ({ sheet, index }) => {
        // Get the state for this sheet
        const sheetData = this.state.excel.sheetsData[index] || {
            numberCol: '',
            uidCol: '',
            braceletsPerPack: this.state.type == 'fabric' ? 100 : 125,
            ranges: null,
            hasHeader: false,
        };

        // Function to update the state of a tab
        const setSheetData = (newData) => {
            const updatedSheetsData = [...this.state.excel.sheetsData];
            updatedSheetsData[index] = { ...sheetData, ...newData };
            this.setState({
                excel: {
                    ...this.state.excel,
                    sheetsData: updatedSheetsData
                }
            });
        };

        // Enable ranges
        const enableRanges = () => {
            setSheetData({ ranges: [{ from: "", to: "" }] });
        };

        // Add a range
        const addRange = () => {
            setSheetData({ ranges: [...sheetData.ranges, { from: "", to: "" }] });
        };

        // Update a range
        const updateRange = (index, field, value) => {
            const updated = [...sheetData.ranges];
            updated[index][field] = value;
            setSheetData({ ranges: updated });
        };

        // Remove a range
        const removeRange = (index) => {
            const updated = sheetData.ranges.filter((_, i) => i !== index);
            setSheetData({ ranges: updated.length > 0 ? updated : null });
        };

        return (
            <Box role="tabpanel" hidden={this.state.tabValue !== index} id={`simple-tabpanel-${index}`} aria-labelledby={`simple-tab-${index}`} sx={{ flexGrow: 1, paddingTop: 3}}>
                <Grid container spacing={2}>
                    {/* Show tab information */}
                    <Grid item xs={12}>
                        <Typography>{sheet.rowCount} {translate('custom.total_rows')}</Typography>
                    </Grid>

                    {/* Column selectors */}
                    <Grid item md={6} xs={12}>
                        <TextField
                            select
                            label="Number"
                            value={sheetData.numberCol}
                            onChange={(e) => setSheetData({ ...sheetData, numberCol: e.target.value })}
                            fullWidth
                        >
                            {sheet.headerValues.map((val, idx) => (
                                <MenuItem key={idx} value={val}>
                                    {val}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item md={6} xs={12}>
                        <TextField
                            select
                            label="UID"
                            value={sheetData.uidCol}
                            onChange={(e) => setSheetData({ ...sheetData, uidCol: e.target.value })}
                            fullWidth
                        >
                            {sheet.headerValues.map((val, idx) => (
                                <MenuItem key={idx} value={val}>
                                    {val}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    {/* Bracelets per Pack and Ranges */}
                    <Grid item md={3} xs={6}>
                        <TextField
                            type="number"
                            label="Bracelets per Pack"
                            value={sheetData.braceletsPerPack}
                            onChange={(e) => setSheetData({ braceletsPerPack: Number(e.target.value) })}
                            sx={{ width: '100%' }}
                        />
                    </Grid>
                    <Grid item md={3} xs={6}>
                        <FormControlLabel control={<Checkbox checked={sheetData.hasHeader} onChange={(e) => setSheetData({ ...sheetData, hasHeader: e.target.checked })} sx={{ '& .MuiSvgIcon-root': { fontSize: 28 } }} />} label={translate('custom.has_header')} />
                    </Grid>

                    <Grid item md={6} xs={12}>
                        {sheetData.ranges == null ? (
                            <Button sx={{ width: '100%' }} variant='contained' color='primary' onClick={enableRanges}>
                                {translate('custom.enable_partial_import')}
                            </Button>
                        ) : (
                            <div>
                                {sheetData.ranges?.map((r, index) => (
                                    <div
                                        key={index}
                                        style={{
                                            display: "flex",
                                            alignItems: "stretch",
                                            gap: 10,
                                            marginBottom: 8
                                        }}
                                    >
                                        <TextField
                                            type="number"
                                            placeholder={translate('custom.from')}
                                            value={r.froms}
                                            onChange={(e) => updateRange(index, "from", e.target.value)}
                                            sx={{ width: '100%' }}
                                        />
                                        <TextField
                                            type="number"
                                            placeholder={translate('custom.to')}
                                            value={r.to}
                                            onChange={(e) => updateRange(index, "to", e.target.value)}
                                            sx={{ width: '100%' }}
                                        />

                                        <Button variant='outlined' sx={{height: '56px'}} color='primary' onClick={() => removeRange(index)}>
                                            -
                                        </Button>
                                    </div>
                                ))}

                                <Button sx={{ width: '100%' }} variant='contained' color='primary' onClick={addRange}>
                                    {translate('custom.add_another_range')}
                                </Button>
                            </div>
                        )}
                    </Grid>
                </Grid>
            </Box>
        );
    };


    handleChange(event, newValue) {
        this.setState({
            tabValue: newValue
        })
    };

    closeTab(index) {
        let sheets = this.state.excel.sheets;
        sheets.splice(index, 1);
        if(sheets.length === 0) {
            this.setState({
                excel: null,
                tabValue: 0
            });
            return;
        }
        this.setState({
            excel: {
                ...this.state.excel,
                sheets: sheets,
                sheetsData: this.state.excel.sheetsData.filter((_, i) => i !== index)
            },
            tabValue: 0
        });
    }

    render() {
        return (
            <V2Layout
                currentMenu='wristbands'
                title={this.state.title}
            >

                {(this.state.loading) ? (
                    <Box sx={{ width: '100%' }}>
                        <LinearProgress />
                    </Box>
                ) : null}

                <Paper sx={{mt: 2, padding: 2}}>
                    { this.state.excel != null  ? null :
                        <div style={{textAlign: "center", margin: "auto"}}>
                            <div>
                                <FileOpenIcon sx={{ width: '200px', height: '200px', margin: 'auto', color: 'lightgray'}} />
                            </div>
                            <Typography variant='h7' sx={{mb: 2, display: 'inline-block', ml: 1}}>{translate('custom.select_excel_file_to_import_wristband_packets')}</Typography>
                        </div>
                    }
                    <InputFile handleChange={this.handleFileChange.bind(this)} sx={{ margin: 'auto', display: 'block' }} />
                    { this.state.excel == null ? null : 
                        <>
                            <h2>{this.state.excel.fileName}</h2>                                
                            <Box sx={{ width: '100%' }}>
                                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                                    <Tabs value={this.state.tabValue} onChange={this.handleChange.bind(this)}>
                                        {
                                            this.state.excel.sheets.map((sheet, index) => (
                                                <Tab label={<div style={{display: 'flex', alignItems: 'center', gap: '3px'}}><h3>{sheet.sheetName}</h3> <CloseIcon onClick={() => this.closeTab(index)}/></div>} {...this.a11yProps(index)} key={index} onMouseDown={(event) => {if(event.button === 1) this.closeTab(index)}} />
                                            ))
                                        }
                                    </Tabs>
                                </Box>
                                {
                                    this.state.excel.sheets.map((sheet, index) => (
                                        <this.SheetTabPanel sheet={sheet} index={index} key={index}> </this.SheetTabPanel>
                                    ))
                                }
                            </Box>
                            <Grid container spacing={2} sx={{mt: 2, justifyContent: 'space-around'}}>
                                <Grid item xs={12}>
                                    <Stack direction='row' spacing={2}>
                                        <Button variant='contained' color='primary'
                                            onClick={async () => {
                                                await this.import();
                                            }}
                                            disabled={this.state.loading}
                                        >
                                            {translate('custom.import')}
                                        </Button>
                                    </Stack>
                                </Grid>
                            </Grid>
                        </>
                    }
                </Paper>
            </V2Layout>
        );
    }
}

export default ImportWristbandPacket;
